import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { BUSINESS } from '../../mocks/sharedData';

interface TestimonialsSectionProps {
  eyebrow?: string;
  headline?: string;
}

const REVIEW_CARDS = [
  {
    platform: 'Google',
    icon: 'ri-google-fill',
    rating: 5,
    summary: 'Exceptional service from start to finish',
    body: 'Our Kentuckiana neighbors are consistently giving Star Solar Specialists 5-star ratings across Google. From the initial consultation in Jeffersonville to final grid connection, customers praise the team\'s professionalism, communication, and quality workmanship.',
    cta: 'Read Google Reviews',
    href: 'https://www.google.com/search?q=Star+Solar+Specialists+Jeffersonville+IN+reviews',
    accentBg: 'from-amber-500/10 to-amber-600/5',
  },
  {
    platform: 'Facebook',
    icon: 'ri-facebook-fill',
    rating: 5,
    summary: 'Trusted by hundreds of homeowners',
    body: 'Hundreds of Southern Indiana and North Central Kentucky homeowners have shared their solar experiences on Facebook. The community feedback consistently highlights Star Solar\'s transparent pricing, clean installations, and ongoing support.',
    cta: 'View Facebook Reviews',
    href: BUSINESS.social.facebook,
    accentBg: 'from-blue-500/10 to-blue-600/5',
  },
  {
    platform: 'Direct Testimonials',
    icon: 'ri-chat-quote-fill',
    rating: 5,
    summary: 'Real stories from real customers',
    body: 'From Jeffersonville homeowners who cut their electric bills to zero, to commercial clients in Louisville who reduced overhead costs — read real testimonials from customers across the entire Kentuckiana region.',
    cta: 'Read All Testimonials',
    href: 'https://starsolarspecialists.com/testimonials/',
    accentBg: 'from-amber-500/10 to-orange-500/5',
  },
];

const TestimonialsSection = ({
  eyebrow = 'Customer Reviews',
  headline = 'Trusted Across Kentuckiana',
}: TestimonialsSectionProps) => {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section className="bg-white py-32">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div
          ref={ref}
          className={`max-w-3xl mx-auto text-center mb-8 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="w-10 h-px bg-amber-500" />
            <span className="text-amber-600 text-xs font-semibold tracking-[0.25em] uppercase">{eyebrow}</span>
            <div className="w-10 h-px bg-amber-500" />
          </div>
          <h2 className="font-serif text-5xl md:text-6xl text-zinc-900 leading-tight mb-5">{headline}</h2>
          <p className="text-zinc-500 text-lg leading-relaxed">
            Since 2015, homeowners and businesses across Southern Indiana and North Central Kentucky have
            trusted Star Solar Specialists for professional, affordable solar installations.
          </p>
        </div>

        {/* Overall rating badge */}
        <div
          className={`flex justify-center mb-16 transition-all duration-700 delay-100 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <div className="inline-flex items-center gap-4 bg-amber-50 border-2 border-amber-200 rounded-full px-8 py-4">
            <div className="flex">
              {Array(5).fill(null).map((_, i) => (
                <div key={i} className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-star-fill text-amber-500 text-base" />
                </div>
              ))}
            </div>
            <div className="w-px h-6 bg-amber-200" />
            <div>
              <span className="text-zinc-800 font-bold text-sm">5-Star Rated</span>
              <span className="text-zinc-500 text-sm"> · Jeffersonville's Top Solar Installer</span>
            </div>
          </div>
        </div>

        {/* Review cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {REVIEW_CARDS.map((card, i) => (
            <div
              key={card.platform}
              className={`bg-stone-50 rounded-3xl p-8 border border-stone-100 flex flex-col hover:border-amber-200 transition-all duration-700 hover:-translate-y-1 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${200 + i * 120}ms` }}
            >
              {/* Platform header */}
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 flex items-center justify-center bg-white border border-stone-200 rounded-2xl">
                  <i className={`${card.icon} text-zinc-700 text-xl`} />
                </div>
                <div>
                  <span className="font-bold text-zinc-800 text-sm block">{card.platform}</span>
                  <div className="flex mt-0.5">
                    {Array(card.rating).fill(null).map((_, si) => (
                      <div key={si} className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-star-fill text-amber-500 text-xs" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Quote mark */}
              <div className="w-8 h-8 flex items-center justify-center mb-3 opacity-20">
                <i className="ri-double-quotes-l text-amber-500 text-3xl" />
              </div>

              <h3 className="font-serif text-lg text-zinc-900 font-semibold mb-3">&ldquo;{card.summary}&rdquo;</h3>
              <p className="text-zinc-500 text-sm leading-relaxed flex-1 mb-6">{card.body}</p>

              <a
                href={card.href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-amber-600 hover:text-amber-700 font-semibold text-sm transition-colors cursor-pointer"
              >
                {card.cta}
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-external-link-line" />
                </div>
              </a>
            </div>
          ))}
        </div>

        {/* Post-testimonial CTA strip */}
        <div
          className={`bg-zinc-950 rounded-3xl overflow-hidden transition-all duration-700 delay-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="relative px-10 py-12 flex flex-col md:flex-row items-center justify-between gap-8">
            {/* Glow */}
            <div className="absolute top-0 left-1/4 w-64 h-32 bg-amber-500/10 blur-3xl pointer-events-none" />

            <div className="relative z-10">
              <p className="font-serif text-3xl text-white mb-2">Join Hundreds of Satisfied Customers</p>
              <p className="text-zinc-400 text-base">Find out why Kentuckiana homeowners trust Star Solar Specialists.</p>
            </div>
            <div className="relative z-10 flex flex-col sm:flex-row gap-3 flex-shrink-0">
              <a
                href={BUSINESS.consultationUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold text-sm px-8 py-4 rounded-full whitespace-nowrap transition-all duration-300 cursor-pointer"
              >
                Get My Free Estimate
              </a>
              <Link
                to="/about"
                className="inline-flex items-center justify-center border border-zinc-700 hover:border-zinc-400 text-zinc-300 hover:text-white font-semibold text-sm px-8 py-4 rounded-full whitespace-nowrap transition-all duration-300 cursor-pointer"
              >
                Our Story
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
